/**
 * Money convention for the whole API contract.
 *
 * RULE: every monetary value crosses the wire as an **integer in the currency's
 * minor unit** — BDT poisha (×100), USD/GBP/EUR cents (×100) — with its
 * `currencyCode` travelling alongside. No floats, ever. Display formatting
 * (symbol, grouping, the Mercury superscript-cents look) happens only at the
 * render edge via lib/format.ts.
 *
 * The current mock fixtures still store whole-taka MAJOR units (e.g. 1500 for
 * ৳1,500). The mechanical fixture sweep multiplies those by 100; until then the
 * api seam adapts at the boundary. See docs/API_CONTRACT.md §Money.
 */
import { z } from 'zod';
import { currencyCodeSchema, type CurrencyCode } from './enums';

/** A money value as it appears on an entity: integer minor units + currency. */
export const moneySchema = z.object({
  amountMinor: z.number().int(),
  currencyCode: currencyCodeSchema,
});
export type Money = z.infer<typeof moneySchema>;

const MINOR_PER_MAJOR = 100;

/** Major units (1500.50) → minor units (150050). Rounds half-up. */
export function toMinor(major: number): number {
  return Math.round(major * MINOR_PER_MAJOR);
}

/** Minor units (150050) → major units (1500.5) for display only. */
export function toMajor(minor: number): number {
  return minor / MINOR_PER_MAJOR;
}

/** Build a Money from a major-unit fixture value (used by the api seam adapters). */
export function moneyFromMajor(major: number, currencyCode: CurrencyCode = 'BDT'): Money {
  return { amountMinor: toMinor(major), currencyCode };
}
